import { AxiosError } from 'axios'
import { NctsApiError } from '../../routes/phase5/TransitOperationEditor/hooks/useTransitOperationContext'
import { BadRequestResponse, EntityNotFoundResponse } from '../../types/Errors'

export type ErrorItem = NctsApiError | BadRequestResponse

type GenericErrorResponse = {
  id: string
  message: string
  errors: ErrorItem[]
}

export type ErrorResponse = GenericErrorResponse | EntityNotFoundResponse

function isBadRequestError(item: ErrorItem): item is BadRequestResponse {
  return 'reason' in item && 'message' in item
}

function isNctsError(item: ErrorItem): item is NctsApiError {
  return 'description' in item && !('reason' in item)
}

function isGenericErrorResponse(item: ErrorResponse): item is GenericErrorResponse {
  return 'errors' in item
}

function isNotFoundErrorResponse(item: ErrorResponse): item is EntityNotFoundResponse {
  return 'entity' in item
}

export type ApiError = {
  traceId: string
  message: string
  response?: ErrorResponse
}

export interface ParsedErrorResponse {
  traceId: string | null
  constraintViolations: BadRequestResponse[]
  nctsErrors: NctsApiError[]
  notFoundError: EntityNotFoundResponse | null
  generalMessage?: string
  isConstraintViolation: boolean
  isNotFoundError: boolean
  isNctsError: boolean
}

export function parseErrorResponse(data: ErrorResponse | undefined, status: number | undefined) {
  const result: ParsedErrorResponse = {
    traceId: null,
    constraintViolations: [],
    nctsErrors: [],
    notFoundError: null,
    generalMessage: data?.message,
    isConstraintViolation: false,
    isNotFoundError: false,
    isNctsError: false,
  }

  if (!data) return result

  result.traceId = data.id

  if (isGenericErrorResponse(data)) {
    const items: ErrorItem[] = data.errors ?? []
    const constraintViolations = items.filter(isBadRequestError)
    const nctsErrors = items.filter(isNctsError)

    if (status === 400 && data.message === 'CONSTRAINT_VIOLATION') {
      result.constraintViolations = constraintViolations
      result.isConstraintViolation = constraintViolations.length > 0
    }

    if (data.message?.startsWith('Prepared request contains') || nctsErrors.length > 0) {
      result.nctsErrors = nctsErrors
      result.isNctsError = nctsErrors.length > 0
    }
  }

  if (status === 404 && isNotFoundErrorResponse(data)) {
    result.isNotFoundError = true
    result.notFoundError = data
  }

  return result
}

export function parseAxiosTransitErrorResponse(error: AxiosError<ErrorResponse>) {
  const data = error.response?.data
  const status = error.response?.status

  return parseErrorResponse(data, status)
}
